import {
    IBetWinData,
    IGameData,
    IInitData,
    IReplayNextPlayerInteraction,
    IServerData,
    ISymbolValues
} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {IParsedStackedSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {WGParsedGameData} from "./data/WGParsedGameData";
import {WGGameConfig} from "../WGGameConfig";
import {WGSpriteSymbol} from "../engine/Symbol/WGSpriteSymbol";
import {WGSymbolData} from "../engine/Symbol/WGSymbolData";
import {SpinServerDataParser} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinServerDataParser";
import {IBlankGameData} from "./data/IBlankServerData";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";

export class WGServerDataParser extends SpinServerDataParser {
    public parseMode(rawData: IServerData): string {
        return rawData.mode;
    }

    public parseExtraSymbolOnTop(rawData: WGParsedGameData): string[][] {
        return rawData.extraSymbolsOnTop!;
    }

    public parseNextMode(rawData: IServerData): string {
        return rawData.nextMode;
    }

    public parseReels(rawData: IServerData): string[][] {
        return WGServerDataParser.parseStackedSymbolsInReels(rawData.reels);
    }

    public static parseStackedSymbolsInReels(rawReels: string[][], parsedSymbolNamesUnder?: string[], actualReels: boolean = false): string[][] {
        let result: string[][] = [];
        for (let i: number = 0; i < rawReels.length; i++) {
            if (WGGameConfig.instance.WG_STACKED_SYMBOLS == null) {
                result.push(rawReels[i].concat());
            } else {
                result.push(this.parseStackedSymbolsInReel(rawReels[i], i, parsedSymbolNamesUnder == null ? undefined : parsedSymbolNamesUnder[i]));
                // added this for loop to filter stacks as per reel
                //different stack of wild for different reels
                for (let j: number = 0; j < result[i].length; j++) {
                    if (i >= 2 && i < 5) {
                        if (result[i][j].indexOf("W") > -1) {
                            let symName = result[i][j].split("*");
                            symName[0] = "W" + (i - 1).toString()
                            result[i][j] = symName.join("*");
                        }
                    }
                }
            }
            if (!actualReels) {
                result[i] = WGServerDataParser.updateStopReelSet(result[i]);
            }
        }
        return result;
    }

    /**
     * function to return reelSet with complete parts of stack
     */
    public static updateStopReelSet(reelSet: string[]): string[] {
        let finalReelSet: string[] = [...reelSet];
        let stackSymPresent: boolean = WGSymbolData.stackSymbols.indexOf(finalReelSet[0].split("*")[0]) > -1;
        for (let i: number = 0; i < 2; i++) {
            if (stackSymPresent && Number(finalReelSet[0].split("*")[1]) > 0) {
                let sym: string = finalReelSet[0].split("*")[0] + "*" + (Number(finalReelSet[0].split("*")[1]) - 1).toString();
                finalReelSet.unshift(sym);
            } else {
                const index: number = Math.floor((Math.random() * 5) + 1);
                finalReelSet.unshift("L" + index.toString());
            }
        }
        stackSymPresent = WGSymbolData.stackSymbols.indexOf(finalReelSet[finalReelSet.length - 1].split("*")[0]) > -1;
        for (let i: number = 0; i < 2; i++) {
            if (stackSymPresent && Number(finalReelSet[finalReelSet.length - 1].split("*")[1]) < 2) {
                let sym: string = finalReelSet[finalReelSet.length - 1].split("*")[0] + "*" + (Number(finalReelSet[finalReelSet.length - 1].split("*")[1]) + 1).toString();
                finalReelSet.push(sym);
            } else {
                const index: number = Math.floor((Math.random() * 5) + 1);
                finalReelSet.push("L" + index.toString());
            }
        }

        return finalReelSet;
    }

    // Always start to parse the reel from the bottom because in all cases the bottom of the reel is already known.
    // For special cases like the reel is up side down, please create your own parsing.
    public static parseStackedSymbolsInReel(rawReel: string[], reelIndex: number, parsedSymbolNameUnder?: string): string[] {
        const input: string[] = rawReel.concat();
        const result: string[] = [];
        if (parsedSymbolNameUnder == null) {
            let bottomStackedSymName: IParsedStackedSymbolName = WGSpriteSymbol.parseStackedSymbolName(input[input.length - 1], reelIndex);
            // Find out if the reel has half stacked symbols in the bottom
            if (bottomStackedSymName.isStacked) {
                let i: number;
                for (i = 1; i < input.length; i++) {
                    const testStackedSymName: IParsedStackedSymbolName = WGSpriteSymbol.parseStackedSymbolName(input[input.length - 1 - i], reelIndex);
                    if (testStackedSymName.isStacked && testStackedSymName.symName === bottomStackedSymName.symName) {
                        bottomStackedSymName = testStackedSymName;
                    } else {
                        break;
                    }
                }
                // i is the first symbol that's not in the stack.
                i = i % bottomStackedSymName.totalNum; // If num of stacked symbols in the bottom is bigger than stacked.totalNum.
                parsedSymbolNameUnder = `${bottomStackedSymName.symName}*${i.toString()}`;
            } else {
                parsedSymbolNameUnder = "NonStackedDummySym";
            }
        }
        while (input.length > 0) {
            const stackedSymNameUnder: IParsedStackedSymbolName = WGSpriteSymbol.parseStackedSymbolName(result.length > 0 ? result[0] : parsedSymbolNameUnder, reelIndex);
            const stackedSymName: IParsedStackedSymbolName = WGSpriteSymbol.parseStackedSymbolName(input.pop()!, reelIndex);

            // For continue stacked symbol
            if (stackedSymNameUnder.isStacked && stackedSymNameUnder.index > 0) {
                if (!stackedSymName.isStacked || stackedSymName.symName !== stackedSymNameUnder.symName) {
                    debugger;
                    Logger.logDev("If you are using the outcome setter, this might be a case that the outcome made an illegal server data. Please take to the server dev to check if that worth a fix.");
                    throw new Error(`ServerDataParser.parseStackedSymbolsInReel():reelId = ${reelIndex}, reelData = ${rawReel.toString()}, symName = ${stackedSymName.symName}, the symbol should be ${stackedSymName.totalNum}-stacked!`);
                }
                result.unshift(`${stackedSymName.symName}*${(stackedSymNameUnder.index - 1).toString()}`);
                continue;
            }
            // For starting a new stacked symbol
            if (stackedSymName.isStacked) {
                result.unshift(`${stackedSymName.symName}*${(stackedSymName.totalNum - 1).toString()}`);
                continue;
            }
            // For non stacked symbol
            result.unshift(stackedSymName.symName);
        }

        return result;
    }

    public parseNextReelSetName(rawData: IServerData): string {
        return rawData.reelsNextSpin;
    }

    public parseInitReelSet(rawData: IInitData): string {
        if (rawData.initReelSet) {
            return rawData.initReelSet;
        }

        Logger.logDev("ServerDataParser.parseInitReelSet(): initReelSet is missing, use nextReelSetName for init. This might not be a problem if your game doesn't have full Stacked reel.");
        return this.parseNextReelSetName(rawData);
    }

    public parseTotalWin(rawData: IServerData): number {
        if (rawData.accumulatedRoundWin != null) {
            return parseFloat(rawData.accumulatedRoundWin);
        }
        return 0;
    }

    public parseCurrentMultiplier(rawData: IServerData): number {
        return rawData.currentMultiplier != null ? rawData.currentMultiplier : 1;
    }

    public parseOverlayWilds(rawData: IServerData): boolean[][] | undefined {
        return rawData.overlayWilds;
    }

    public parseFreespinsLeft(rawData: IServerData): number {
        return rawData.freespinsLeft != null ? rawData.freespinsLeft : 0;
    }

    public parseFreespinTriggeredThisSpin(rawData: IServerData): boolean {
        return rawData.freespinTriggeredThisSpin != null ? rawData.freespinTriggeredThisSpin : false;
    }

    public parseAddedNumberOfFreespinsThisSpin(rawData: IServerData): number {
        return rawData.addedNumberOfFreespinsThisSpin != null ? rawData.addedNumberOfFreespinsThisSpin : 0;
    }

    public parseReplayNextPlayerInteraction(rawData: IServerData): IReplayNextPlayerInteraction | undefined {
        if (!GameSetting.replayMode) {
            return undefined;
        }

        return rawData.nextPlayerInteraction;
    }

    public parseScatterPositionsEnteringFreespin(rawData: IInitData | IGameData): number[] | undefined {
        return rawData.scatterPositionsEnteringFreespin;
    }

    // Init Data:
    public parseBetLines(rawData: IInitData): number[][] {
        return rawData.betLines;
    }

    public parseSymbolValues(rawData: IInitData): ISymbolValues {
        return rawData.symbolValues;
    }

    public parseIsRestoreState(rawData: IInitData): boolean {
        return rawData.isRestoreState;
    }

    // Game Data:
    public parseBetWins(rawData: IGameData): IBetWinData[] {
        switch (GameConfig.instance.BET_WIN_MODE) {
            case BetWinMode.BET_LINE:
                return rawData.betLineWins!;
            case BetWinMode.BET_WAY:
                return rawData.betWayWins!;
            default:
                debugger;
                throw new Error("ServerDataParser.parseBetWins(): Invalid BetWinMode from GameConfig!");
        }
    }

    public parseSingleWin(rawData: IGameData): number {
        return parseFloat(rawData.totalSpinWinnings);
    }

    public parsePlayedBetValue(rawData: IGameData): number {
        return parseFloat(rawData.playedBetValue);
    }

    public parseScatterPositions(rawData: IGameData): number[] | undefined {
        return rawData.scatterPositions;
    }

    public parseReelsEnteringFreespin(rawData: IGameData): string[][] | undefined {
        if (rawData.reelsEnteringFreespin != undefined) {
            return WGServerDataParser.parseStackedSymbolsInReels(rawData.reelsEnteringFreespin);
        }
        return undefined;
    }

    public parseBetLineWinsEnteringFreespin(rawData: IGameData): IBetWinData[] | undefined {
        return rawData.betLineWinsEnteringFreespin;
    }

    public parseNumberOfFreespinsPlayed(rawData: IServerData): number | undefined {
        return rawData.numberOfFreespinsPlayed;
    }

    public parseNearWinWarrior(rawdata: IBlankGameData): boolean[] {
        const retVal: boolean[] = rawdata.nearWinWarrior,
            reels: string[][] = rawdata.reels,
            midSyms: string[] = (rawdata.triggerWarriorSymbol.length > 0) ? [rawdata.triggerWarriorSymbol] : ["M1", "M2", "M3", "M4", "M5"];

        let reel: string[],
            idx: number = -1,
            count: number = 0;

        if (rawdata.mode === GameMode.FREESPIN && rawdata.superFreespin) {
            for (let i: number = 0; i < midSyms.length; i++) {
                count = 0;
                for (let j: number = 0; j < reels.length; j++) {
                    reel = reels[j];

                    //skipping locked reels and jumping to next reel.
                    if (rawdata.lockedFSWildMultiplier[j] > 0) {
                        continue;
                    }

                    // checking only visible area.
                    for (let k: number = 1; k < (reel.length - 1); k++) {
                        if (midSyms[i] === reel[k]) {
                            count++;
                            break; // since every mid sym is stacked so break can be used.
                        }
                    }

                    // min. 2 same mid stack syms are required.
                    if (count > 1) {
                        idx = j;
                        break;
                    }
                }
                if (idx !== -1) {
                    break;
                }
            }
        }

        if (idx !== -1) {
            for (let i: number = (idx + 1); i < retVal.length; i++) {
                retVal[i] = true;
            }
        }

        return retVal;
    }
}